import React, { useRef, useState, useEffect } from 'react';
import './BottomInput.css';
import SuccessModal from './SuccessModal';

const BottomInput = ({ onToast }) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = 24;
      const maxLines = 5;
      const maxHeight = lineHeight * maxLines;
      
      if (scrollHeight > maxHeight) {
        textarea.style.height = maxHeight + 'px';
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.height = scrollHeight + 'px';
        textarea.style.overflowY = 'hidden';
      }
    }
  };

  const handleFileSelect = (files) => {
    const newFiles = Array.from(files).map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      name: file.name,
      type: file.type
    }));
    setAttachments(prev => [...prev, ...newFiles]);
  };

  const handleFileInput = (e) => {
    if (e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
  };

  const removeAttachment = (id) => {
    setAttachments(prev => {
      const filtered = prev.filter(att => att.id !== id);
      // Clean up object URLs
      const removed = prev.find(att => att.id === id);
      if (removed && removed.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return filtered;
    });
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleSend = () => {
    if (message.trim() || attachments.length > 0) {
      setShowSuccess(true);
      setMessage('');
      setAttachments([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getFileIcon = (type) => {
    if (type.includes('pdf')) return '📄';
    if (type.includes('word') || type.includes('doc')) return '📝';
    if (type.includes('text')) return '📃';
    if (type.includes('spreadsheet') || type.includes('excel')) return '📊';
    return '📎';
  };

  return (
    <>
      <div className="bottom-input-container">
        {attachments.length > 0 && (
          <div className="attachments-preview">
            {attachments.map(att => (
              <div key={att.id} className="attachment-chip">
                {att.preview ? (
                  <img src={att.preview} alt={att.name} className="attachment-image" />
                ) : (
                  <div className="attachment-doc">
                    <span className="doc-icon">{getFileIcon(att.type)}</span>
                  </div>
                )}
                <span className="attachment-name">{att.name}</span>
                <button 
                  className="remove-attachment"
                  onClick={() => removeAttachment(att.id)}
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <div 
          className={`input-wrapper ${isDragging ? 'dragging' : ''} ${message ? 'has-content' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {isDragging && (
            <div className="drag-overlay">
              <div className="drag-text">📎 Drop files here</div>
            </div>
          )}
          
          <button 
            className="attach-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Attach file"
          >
            📎
          </button>
          
          <textarea
            ref={textareaRef}
            className="message-input"
            placeholder="Give your requirement..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            rows="1"
          />
          
          <button 
            className="send-btn"
            onClick={handleSend}
            title="Send message"
            disabled={!message.trim() && attachments.length === 0}
          >
            ➤
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.xls"
            onChange={handleFileInput}
          />
        </div>
      </div>

      {showSuccess && (
        <SuccessModal 
          message="We'll get back to you shortly."
          onClose={() => setShowSuccess(false)}
        />
      )}
    </>
  );
};

export default BottomInput;
